// The map generator (§31 D3): zones assembled into whole boards.
//
// **A config is a name; the seed is the only free parameter.** A generated
// map crosses the wire as its `BoardMap` id, exactly as every other map does
// (`maps.ts`), and both ends expand it here: `layoutMap(config, seed)` places
// zone rectangles, and each grows off its own stream (`zones.ts`). So a log
// carries one word and a seed, never a rectangle or a unit of ore.
//
// **Fair by construction.** A config fills White's half — `region`, whose
// ranks stop at 3 — and every zone is laid again turned half about the
// classic centre for Black, contents and all (`rotateYield`). The classic
// sixty-four is never taken, so the opening FEN stands on the board it always
// did and both kings start legal.
//
// **Joined, never floating.** A zone is placed only where its floor touches
// ground already laid (`touches`), so every square a piece can stand on is
// reachable by walking from the armies.
//
// **Coal wherever metal** (§31 M2): every config's first slot is a Quarry or
// a Deposit Zone, both of which always spawn coal, and it is placed first so
// it has the room.
//
// **The board grows by zones** too: `growthRow` picks a lobe beside a
// landing camp off the config's `growth` pool, and `next-stage.zones` lays it
// (`growZones`) — additive, like every other stage ground.

import { type Bounds, CLASSIC_BOUNDS, classicCells, fileOf, rankOf, square } from "./board";
import { hasStructure, type IndustryState } from "./industry/state";
import { ISLAND_LAYOUT_COUNT } from "./islands";
import { boardMap } from "./maps";
import { hasCell, type Position, setCells } from "./position";
import { streamFor } from "./random";
import { IllegalCommandError } from "./reduce";
import {
  applyZone,
  rectSquares,
  rotateYield,
  touches,
  zoneFault,
  zoneFloor,
  type ZoneRow,
  ZONE_SPECS,
  zoneYield,
  type ZoneYield
} from "./zones";
import {
  ZONE_BATTLEFIELD,
  ZONE_BROKEN,
  ZONE_CANYON,
  ZONE_DEPOSIT,
  ZONE_DIAMOND,
  ZONE_FIELD,
  ZONE_GOLDEN,
  ZONE_QUARRY,
  ZONE_VOID,
  type ZoneKind
} from "src/shared/zones";

/**
 * The generator's salts, after the zones' 137/138 and clear of every other in
 * the repo: the layout draw, a generated map's zone contents, a stage lobe's
 * choice, a stage's zone contents, a level region's zone contents.
 */
export const MAPGEN_LAYOUT_STREAM = 139;
export const MAPGEN_ZONE_STREAM = 140;
export const MAPGEN_GROWTH_STREAM = 141;
export const STAGE_ZONE_STREAM = 142;
export const REGION_ZONE_STREAM = 143;

/** The most zone rows one command may carry — and the stride of each door's stream index. */
export const ZONE_ROWS_MAX = 16;

/** The longest side a stage lobe may have: a 20×20 Broken Lands is a map, not a lobe. */
export const GROWTH_SIDE_MAX = 10;

export type MapGenId = "frontier" | "expanse";

/** One draw of the layout: so many zones, each a kind off the pool. */
export interface MapGenSlot {
  pool: readonly ZoneKind[];
  count: readonly [min: number, max: number];
}

/** A map-maker's config: where White's zones go and what they may be. */
export interface MapGenConfig {
  id: MapGenId;
  /** White's ground to fill, inclusive. Never past rank 3; classic squares are never taken. */
  region: Bounds;
  /** Drawn in order; the first is the coal guarantee (Quarry or Deposit, at least one). */
  slots: readonly MapGenSlot[];
  /** What a stage's lobe draws from (`growthRow`). */
  growth: readonly ZoneKind[];
}

export const MAP_GENS: Record<MapGenId, MapGenConfig> = {
  // The classic ranks and a six-file flank each side: chess's shape, so the
  // wall still promotes.
  frontier: {
    id: "frontier",
    region: { minFile: -6, maxFile: 13, minRank: 0, maxRank: 3 },
    slots: [
      { pool: [ZONE_QUARRY, ZONE_DEPOSIT], count: [1, 1] },
      { pool: [ZONE_GOLDEN, ZONE_DIAMOND, ZONE_BATTLEFIELD], count: [1, 1] },
      { pool: [ZONE_QUARRY, ZONE_DEPOSIT, ZONE_FIELD, ZONE_GOLDEN], count: [0, 2] }
    ],
    growth: [ZONE_QUARRY, ZONE_DEPOSIT, ZONE_GOLDEN, ZONE_FIELD]
  },
  // Flanks and five ranks behind each army: room for a canyon, an old
  // battlefield or a map-maker's islands.
  expanse: {
    id: "expanse",
    region: { minFile: -7, maxFile: 14, minRank: -5, maxRank: 3 },
    slots: [
      { pool: [ZONE_QUARRY, ZONE_DEPOSIT], count: [1, 1] },
      { pool: [ZONE_DEPOSIT, ZONE_QUARRY], count: [1, 1] },
      { pool: [ZONE_BATTLEFIELD, ZONE_BROKEN, ZONE_CANYON], count: [1, 1] },
      { pool: [ZONE_GOLDEN, ZONE_DIAMOND], count: [1, 2] },
      { pool: [ZONE_FIELD, ZONE_VOID, ZONE_QUARRY], count: [1, 3] }
    ],
    growth: [ZONE_QUARRY, ZONE_DEPOSIT, ZONE_GOLDEN, ZONE_DIAMOND, ZONE_BATTLEFIELD, ZONE_CANYON, ZONE_BROKEN]
  }
};

/** The config a map id names, or undefined for a map that is not generated. */
export function mapGenOf(id?: string): MapGenConfig | undefined {
  const gen = boardMap(id)?.generate;
  return gen ? MAP_GENS[gen] : undefined;
}

const between = (roll: () => number, [min, max]: readonly [number, number]): number =>
  min + Math.floor(roll() * (max - min + 1));

/**
 * The sizes a kind may take, `[w, h]`, each way round, clamped to the room.
 * The doc's: a void 2×2 up to 5×5, a canyon 3×8 / 4×12 / 5×20 (2×5 has no
 * floor between its walls), everything else its `ZONE_SPECS` size.
 */
function sizesFor(kind: ZoneKind, maxW: number, maxH: number): [number, number][] {
  const base: readonly (readonly [number, number])[] =
    kind === ZONE_VOID
      ? [[2, 2], [2, 3], [3, 3], [3, 4], [4, 4], [4, 5], [5, 5]]
      : kind === ZONE_CANYON
        ? [[3, 8], [4, 12], [5, 20]]
        : [ZONE_SPECS[kind].size];
  const out: [number, number][] = [];
  for (const [w, h] of base) {
    for (const [a, b] of [[w, h], [h, w]]) {
      const size: [number, number] = [Math.min(a, maxW), Math.min(b, maxH)];
      if (kind === ZONE_CANYON && Math.min(...size) < 3) continue;
      if (!out.some(([x, y]) => x === size[0] && y === size[1])) out.push(size);
    }
  }
  return out;
}

/**
 * Every place a kind fits, in a deterministic order: inside `window`, on no
 * blocked square, its floor touching `ground`.
 */
function placements(
  kind: ZoneKind,
  layout: number | undefined,
  window: Bounds,
  sizes: readonly [number, number][],
  blocked: (sq: number) => boolean,
  ground: ReadonlySet<number>
): ZoneRow[] {
  const out: ZoneRow[] = [];
  for (const [w, h] of sizes) {
    for (let r = window.minRank; r + h - 1 <= window.maxRank; r++) {
      for (let f = window.minFile; f + w - 1 <= window.maxFile; f++) {
        if (rectSquares(f, r, w, h).some(blocked)) continue;
        const row: ZoneRow = layout === undefined ? [kind, f, r, w, h] : [kind, f, r, w, h, layout];
        const floor = kind === ZONE_VOID ? rectSquares(f, r, w, h) : zoneFloor(row);
        if (floor.length === 0 || !touches(floor, ground)) continue;
        out.push(row);
      }
    }
  }
  return out;
}

/**
 * The placements of the **largest** shape that fits anywhere: the doc's
 * size if there is room for it, else the biggest rectangle inside it that
 * has — a 10×10 Battlefield on a six-file flank is a 6×4 one. Largest area
 * first, then widest, so the answer is deterministic.
 */
function fitted(
  kind: ZoneKind,
  layout: number | undefined,
  window: Bounds,
  sizes: readonly [number, number][],
  blocked: (sq: number) => boolean,
  ground: ReadonlySet<number>
): ZoneRow[] {
  const shapes: [number, number][] = [];
  const seen = new Set<string>();
  for (const [w, h] of sizes) {
    for (let a = 2; a <= w; a++) {
      for (let b = 2; b <= h; b++) {
        if (kind === ZONE_CANYON && Math.min(a, b) < 3) continue;
        const key = `${a},${b}`;
        if (seen.has(key)) continue;
        seen.add(key);
        shapes.push([a, b]);
      }
    }
  }
  shapes.sort((x, y) => y[0] * y[1] - x[0] * x[1] || y[0] - x[0] || y[1] - x[1]);
  for (const shape of shapes) {
    const options = placements(kind, layout, window, [shape], blocked, ground);
    if (options.length > 0) return options;
  }
  return [];
}

/** A Broken Lands' island layout, drawn; nothing for any other kind. */
const layoutFor = (kind: ZoneKind, roll: () => number): number | undefined =>
  kind === ZONE_BROKEN ? Math.floor(roll() * ISLAND_LAYOUT_COUNT) : undefined;

/**
 * White's zones for a config and a seed. Pure: no board is read — the
 * classic sixty-four is the ground they join. A slot that finds no room
 * places nothing.
 */
export function layoutMap(config: MapGenConfig, seed: number): ZoneRow[] {
  const roll = streamFor(seed, 0, MAPGEN_LAYOUT_STREAM);
  const { region } = config;
  const classic = classicCells();
  const taken = new Set<number>();
  const ground = new Set(classic);
  const rows: ZoneRow[] = [];
  const width = region.maxFile - region.minFile + 1;
  const height = region.maxRank - region.minRank + 1;
  for (const slot of config.slots) {
    const count = between(roll, slot.count);
    for (let i = 0; i < count; i++) {
      const kind = slot.pool[Math.floor(roll() * slot.pool.length)];
      const layout = layoutFor(kind, roll);
      const options = fitted(
        kind,
        layout,
        region,
        sizesFor(kind, width, height),
        (sq) => classic.has(sq) || taken.has(sq),
        ground
      );
      if (options.length === 0) continue;
      const row = options[Math.floor(roll() * options.length)];
      rows.push(row);
      for (const sq of rectSquares(row[1], row[2], row[3], row[4])) taken.add(sq);
      for (const sq of kind === ZONE_VOID ? [] : zoneFloor(row)) ground.add(sq);
    }
  }
  return rows;
}

/** Half a turn about the classic board's centre. */
const turn = (sq: number): number =>
  square(CLASSIC_BOUNDS.maxFile - fileOf(sq), CLASSIC_BOUNDS.maxRank - rankOf(sq));

/**
 * Lay one yield as new ground: the rectangle becomes cells where it was not
 * ground before the command (`before`), less its voids, and its contents land
 * only there. **Strictly additive** — a zone never takes or litters ground
 * that already existed, the stage's own rule.
 */
function growYield(
  position: Position,
  industry: IndustryState,
  y: ZoneYield,
  area: readonly number[],
  before: ReadonlySet<number>
): void {
  const voids = new Set(y.voids);
  const fresh = area.filter(
    (sq) => !before.has(sq) && !voids.has(sq) && !hasCell(position, sq) && !hasStructure(industry, sq)
  );
  if (fresh.length > 0) setCells(position, [...position.cells, ...fresh]);
  const mine = (sq: number): boolean => !before.has(sq);
  applyZone(position, industry, {
    labels: y.labels.filter(([sq]) => mine(sq)),
    voids: y.voids.filter(mine),
    deposits: y.deposits.filter(([sq]) => mine(sq)),
    loose: y.loose.filter(([sq]) => mine(sq))
  });
}

/**
 * Grow zone rows as new ground (§31 D3). Row `i` grows off
 * `streamFor(seed, base + i, salt)`; `mirror` lays each again turned half
 * about for Black. **Refused, never defaulted**, for a row this build cannot
 * read or a command carrying more than `ZONE_ROWS_MAX`.
 */
export function growZones(
  position: Position,
  industry: IndustryState,
  rows: readonly ZoneRow[],
  seed: number,
  salt: number,
  base: number,
  mirror = false
): void {
  if (rows.length > ZONE_ROWS_MAX) throw new IllegalCommandError(`at most ${ZONE_ROWS_MAX} zones`);
  for (const row of rows) {
    const fault = zoneFault(row);
    if (fault) throw new IllegalCommandError(`bad zone: ${fault}`);
  }
  const before = new Set(position.cells);
  rows.forEach((row, i) => {
    const y = zoneYield(row, streamFor(seed, base + i, salt));
    const area = rectSquares(row[1], row[2], row[3], row[4]);
    growYield(position, industry, y, area, before);
    if (mirror) growYield(position, industry, rotateYield(y), area.map(turn), before);
  });
}

/**
 * A level region's authored zones (a Tiled `zone` object, §31 D3), grown
 * before the region opens — its stream indexed by the region's place in the
 * level, so a start region and a revealed one never share one.
 */
export function growRegionZones(
  position: Position,
  industry: IndustryState,
  region: { zones?: readonly ZoneRow[] },
  seed: number,
  index: number
): void {
  if (region.zones?.length) {
    growZones(position, industry, region.zones, seed, REGION_ZONE_STREAM, index * ZONE_ROWS_MAX);
  }
}

/** Lay a generated map's zones on a fresh board. */
export function layGeneratedMap(
  position: Position,
  industry: IndustryState,
  config: MapGenConfig,
  seed: number
): void {
  growZones(position, industry, layoutMap(config, seed), seed, MAPGEN_ZONE_STREAM, 0, true);
}

/**
 * A stage's lobe: one zone off the config's `growth` pool, on ground nobody
 * has, its floor touching the landing camp's (`landing`). Undefined when no
 * camp landed or nothing fits.
 */
export function growthRow(
  position: Position,
  industry: IndustryState,
  config: MapGenConfig,
  landing: readonly number[],
  roll: () => number
): ZoneRow | undefined {
  if (landing.length === 0) return undefined;
  const kind = config.growth[Math.floor(roll() * config.growth.length)];
  const layout = layoutFor(kind, roll);
  const camp = new Set(landing);
  const files = landing.map(fileOf);
  const ranks = landing.map(rankOf);
  const reach = GROWTH_SIDE_MAX;
  const window: Bounds = {
    minFile: Math.min(...files) - reach,
    maxFile: Math.max(...files) + reach,
    minRank: Math.min(...ranks) - reach,
    maxRank: Math.max(...ranks) + reach
  };
  const options = fitted(
    kind,
    layout,
    window,
    sizesFor(kind, GROWTH_SIDE_MAX, GROWTH_SIDE_MAX),
    (sq) => hasCell(position, sq) || camp.has(sq) || hasStructure(industry, sq),
    camp
  );
  return options.length > 0 ? options[Math.floor(roll() * options.length)] : undefined;
}
